<template>
  <v-card flat class="listing-owners-tab">
    <v-card-text>
      <div class="d-flex align-center flex-wrap mb-4">
        <div>
          <h3 class="listing-owners-tab__title mb-1">Property Owners</h3>
          <p class="listing-owners-tab__hint mb-0">
            Add owners, remove them, or toggle Current. Changes save with the listing.
          </p>
        </div>
        <v-spacer></v-spacer>
        <v-chip small outlined color="#00004d" class="mt-2 mt-sm-0">
          {{ owners.length }} Owner{{ owners.length === 1 ? '' : 's' }}
        </v-chip>
      </div>

      <v-row align="center" class="mb-2">
        <v-col cols="12" md="9">
          <v-autocomplete
            v-model="selectedOwnerId"
            :items="ownerOptions"
            item-text="full_name"
            item-value="id"
            dense
            outlined
            hide-details
            clearable
            :loading="loadingSearch"
            :search-input.sync="ownersSearch"
            label="Search Owner (min. 3 characters)"
            placeholder="Type name to search…"
            no-data-text="Type at least 3 characters to search"
            @change="onOwnerPicked"
          >
            <template v-slot:append-outer>
              <a target="_blank" href="/contacts-management/contacts?addModule=true" title="Add Contact">
                <v-icon color="#00004d">mdi-plus-circle-outline</v-icon>
              </a>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  <span>{{ item.full_name }}</span>
                  <v-chip
                    v-if="Number(item.is_verified) === 1"
                    x-small
                    color="success"
                    class="ml-1"
                    dark
                  >Verified</v-chip>
                </v-list-item-title>
                <v-list-item-subtitle v-if="item.uid">
                  ID: {{ String(item.uid).toUpperCase() }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="item.contact_phone_titles">
                  Phone(s): {{ item.contact_phone_titles }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" md="3">
          <v-btn
            block
            dark
            color="#00004d"
            height="40"
            :disabled="!selectedOwnerId"
            @click="addSelectedOwner"
          >
            <v-icon left small>mdi-account-plus</v-icon>
            Add Owner
          </v-btn>
        </v-col>
      </v-row>

      <div v-if="!sortedOwners.length" class="listing-owners-tab__empty text-center py-10">
        <v-icon large color="grey lighten-1" class="mb-2">mdi-account-group-outline</v-icon>
        <p class="mb-0 grey--text">No owners yet. Search and add an owner above.</p>
      </div>

      <div v-else class="listing-owners-tab__list mt-4">
        <v-card
          v-for="owner in sortedOwners"
          :key="'lo-' + owner.id"
          outlined
          class="listing-owners-tab__card mb-3"
          :class="{ 'listing-owners-tab__card--current': owner.is_current }"
        >
          <v-card-text class="py-3 px-4">
            <div class="d-flex align-center flex-wrap">
              <div class="listing-owners-tab__avatar mr-3">
                {{ initials(owner.full_name) }}
              </div>
              <div class="flex-grow-1" style="min-width: 160px;">
                <div class="d-flex align-center flex-wrap">
                  <a
                    class="listing-owners-tab__name text-decoration-none"
                    :href="'/contacts-management/contacts?contact=' + owner.id"
                    target="_blank"
                  >{{ owner.full_name }}</a>
                  <v-chip
                    v-if="owner.is_current"
                    class="ml-2"
                    x-small
                    color="success"
                    text-color="white"
                    label
                  >Current</v-chip>
                </div>
                <p v-if="owner.uid" class="mb-0 mt-1 grey--text text--darken-1 caption">
                  ID: {{ String(owner.uid).toUpperCase() }}
                </p>
              </div>

              <div class="d-flex align-center mt-2 mt-sm-0">
                <span class="listing-owners-tab__toggle-label mr-2">Current Owner</span>
                <v-switch
                  :input-value="!!owner.is_current"
                  class="mt-0 pt-0"
                  dense
                  hide-details
                  color="success"
                  @change="toggleCurrent(owner, $event)"
                ></v-switch>
                <v-btn
                  icon
                  small
                  class="ml-2"
                  color="error"
                  title="Remove Owner"
                  @click="confirmRemoveOwner(owner)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {CONTACTS_SIMPLE_LIST} from "@/core/services/store/contacts.module";
import {SET_CONFIRMATION} from "@/core/services/store/confirmation.module";
import {SET_SNACK} from "@/core/services/store/snackbar.module";

export default {
  name: 'ListingOwnersTab',
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedOwnerId: null,
      ownersSearch: null,
      ownerOptions: [],
      loadingSearch: false,
      apiTimeout: null,
      searchRequestId: 0
    };
  },
  computed: {
    owners: {
      get() {
        return Array.isArray(this.value) ? this.value : [];
      },
      set(list) {
        this.$emit('input', list);
      }
    },
    sortedOwners() {
      return this.owners.slice().sort((a, b) => {
        const ac = a && a.is_current ? 1 : 0;
        const bc = b && b.is_current ? 1 : 0;
        if (ac !== bc) {
          return bc - ac;
        }
        return String((a && a.full_name) || '').localeCompare(
          String((b && b.full_name) || ''),
          undefined,
          {sensitivity: 'base'}
        );
      });
    }
  },
  watch: {
    ownersSearch(val) {
      clearTimeout(this.apiTimeout);
      const query = String(val || '').trim();
      if (query.length < 3) {
        return;
      }
      this.apiTimeout = setTimeout(() => {
        this.searchOwners(query);
      }, 900);
    }
  },
  beforeDestroy() {
    clearTimeout(this.apiTimeout);
  },
  methods: {
    initials(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) {
        return '?';
      }
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    normalizeName(name) {
      return String(name || '').replace(/\s+/g, ' ').trim();
    },
    searchOwners(query) {
      const requestId = ++this.searchRequestId;
      this.loadingSearch = true;
      this.$store
        .dispatch(CONTACTS_SIMPLE_LIST, {
          role: 'Real Estate Owners',
          q: query
        })
        .then((data) => {
          if (requestId !== this.searchRequestId) {
            return;
          }
          const list = (data && data.records && data.records.contactsList) || [];
          this.mergeOwnerOptions(list);
          this.loadingSearch = false;
        })
        .catch(() => {
          if (requestId !== this.searchRequestId) {
            return;
          }
          this.loadingSearch = false;
        });
    },
    mergeOwnerOptions(list) {
      const byId = {};
      this.ownerOptions.forEach((item) => {
        if (item && item.id) {
          byId[item.id] = item;
        }
      });
      (list || []).forEach((item) => {
        if (item && item.id) {
          byId[item.id] = item;
        }
      });
      this.ownerOptions = Object.values(byId);
    },
    onOwnerPicked(id) {
      if (id) {
        this.selectedOwnerId = id;
      }
    },
    addSelectedOwner() {
      const id = Number(this.selectedOwnerId);
      if (!id) {
        return;
      }
      if (this.owners.some((o) => Number(o.id) === id)) {
        this.$store.dispatch(SET_SNACK, {
          clr: 'error',
          msg: 'This owner is already on the list.'
        });
        return;
      }
      const contact = this.ownerOptions.find((o) => Number(o.id) === id);
      if (!contact) {
        this.$store.dispatch(SET_SNACK, {
          clr: 'error',
          msg: 'Owner details could not be loaded. Search again.'
        });
        return;
      }
      const next = this.owners.concat([{
        id: Number(contact.id),
        uid: contact.uid || null,
        full_name: this.normalizeName(contact.full_name),
        is_current: true,
        date: null,
        ended_at: null
      }]);
      this.owners = next;
      this.selectedOwnerId = null;
      this.ownersSearch = null;
    },
    toggleCurrent(owner, value) {
      const isCurrent = value === true || value === 1 || value === '1';
      this.owners = this.owners.map((row) => {
        if (Number(row.id) !== Number(owner.id)) {
          return row;
        }
        return Object.assign({}, row, {is_current: isCurrent});
      });
    },
    confirmRemoveOwner(owner) {
      const that = this;
      const name = this.normalizeName((owner && owner.full_name) || 'this owner');
      this.$store.dispatch(SET_CONFIRMATION, {
        title: 'Remove Owner?',
        msg: `Remove ${name} from this listing? Ownership history will be kept.`,
        clr: 'red',
        callback: function () {
          that.removeOwner(owner);
        }
      });
    },
    removeOwner(owner) {
      this.owners = this.owners.filter((row) => Number(row.id) !== Number(owner.id));
    }
  }
};
</script>

<style scoped>
.listing-owners-tab__title {
  color: #00004d;
  font-size: 1.15rem;
  font-weight: 700;
}
.listing-owners-tab__hint {
  color: #6b7280;
  font-size: 0.875rem;
}
.listing-owners-tab__empty {
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  background: #fafafa;
}
.listing-owners-tab__card {
  border-radius: 10px !important;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.listing-owners-tab__card--current {
  border-color: #4caf50 !important;
  background: rgba(76, 175, 80, 0.04);
}
.listing-owners-tab__avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #00004d;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  flex-shrink: 0;
}
.listing-owners-tab__name {
  color: #00004d;
  font-weight: 700;
  font-size: 1.02rem;
}
.listing-owners-tab__toggle-label {
  font-size: 0.8rem;
  color: #4b5563;
  white-space: nowrap;
}
</style>
